import { EditModes } from "Enums";
import { IFilter, IFormDefinition, IControlDefinition } from "Grids/Interfaces";
import { IControl } from "Controls/Interfaces";
import { Form } from "./Form";
import { CoreContext } from "Core";
import { GridSettings } from "Configuration";
import { GridEventTypes, ControlEventTypes } from "Core/Events/EventTypes";
import { CommandService, TextBlockService, ComboboxService, GridDefinitionService, DatabaseImageService } from "Service";
import { IFormData } from "Entities";

/**
 * ## Filter
 *
 * A {@link Form} which is used to filter grids.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a Filter with the name GridName.
 * // In script:
 * @@grd:GridName@@
 *
 * // On page:
 * $dms("GridName")
 *
 * // First Filter on page:
 * $dms(".dms-filter")
 * ```
 */
export class Filter extends Form implements IFilter {
    /**
     * @hidden
     */
    public static wrapperTemplate: string = require("./Templates/Filter.html");

    /**
     * @hidden
     */
    // public static columnTemplate: string = require("./Templates/FilterColumn.html");

    private filterCollapsePanel: JQuery;
    private filterCollapseLink: JQuery;

    /**
     * Create a new Filter.
     * @param formDefinition FormDefinition.
     * @param gridSettings GridSettings.
     * @hidden
     */
    constructor(
        formDefinition: IFormDefinition,
        textBlockCategory: string,
        gridSettings: GridSettings,
        comboboxService: ComboboxService,
        commandService: CommandService,
        gridDefinitionService: GridDefinitionService,
        textBlockService: TextBlockService,
        databaseImageService: DatabaseImageService
    ) {
        super(formDefinition, textBlockCategory, gridSettings, comboboxService, commandService, gridDefinitionService, textBlockService, databaseImageService);
        this.editMode = EditModes.Edit;
        this.isAutoResize = false;
    }

    /**
     * Loads the data for the filter.
     * @hidden
     */
    protected async loadData(): Promise<void> {
        // Initialize view model
        this._viewModel = new kendo.data.Model(this.fields);
        this.initialize();
    }

    /**
     * Get the fieldName from the control.
     * @param ctr ControlDefinition.
     * @hidden
     */
    protected getControlFieldName(ctr: IControlDefinition): string {
        return ctr.FilterParameterName || ctr.ApplicationParameter || ctr.ValueColumnName;
    }

    /**
     * Initialize the filter.
     * @hidden
     */
    protected initialize(): void {
        super.initialize();

        this.filterCollapsePanel = this.jQueryElement.find(".dms-collapse-panel");
        this.filterCollapseLink = this.jQueryElement.find(".dms-collapse-link");

        this.filterCollapseLink.on("click", () => {
            this.toggle(true);
        });
    }

    /**
     * Register additional events for this control.
     * @param control Control where controls can be registered.
     * @hidden
     */
    protected registerControlEvents(ctl: IControl): void {
        super.registerControlEvents(ctl);

        if (this.formDefinition.ClearGridsOnChange) {
            ctl.on(ControlEventTypes.Change, () => this.onFilterControlChanged());
        }
    }

    /**
     * Occurs when a control in the filter was changed.
     */
    private onFilterControlChanged(): void {
        CoreContext.Instance.hideGrids(true);
    }

    /**
     * Gets current filter values.
     * @since 4.0.0
     */
    public getData(): IFormData {
        let data = super.getData();

        let filterData = <IFormData>$.extend(data, this.fixedValues);
        return filterData;
    }

    /**
     * Expand the filter.
     * @param animate Specify wether the animation should be played.
     * @since 4.1.0
     */
    public expand(animate: boolean = true): void {
        this.trigger(GridEventTypes.FilterCollapsing);
        if (animate) {
            kendo
                .fx(this.filterCollapsePanel)
                .expandVertical()
                .play()
                .then(() => setTimeout(() => this.trigger(GridEventTypes.FilterCollapsed)));
        } else {
            this.filterCollapsePanel.show();
            this.trigger(GridEventTypes.FilterCollapsed);
        }
        this.filterCollapseLink.toggleClass("collapsed");
    }

    /**
     * Collapse the filter.
     * @param animate Specify wether the animation should be played.
     * @since 4.1.0
     */
    public collapse(animate: boolean = true): void {
        if (animate) {
            kendo
                .fx(this.filterCollapsePanel)
                .expandVertical()
                .reverse()
                .then(() => this.trigger(GridEventTypes.FilterCollapsed));
        } else {
            this.filterCollapsePanel.hide();
            this.trigger(GridEventTypes.FilterCollapsed);
        }
        this.filterCollapseLink.toggleClass("collapsed");
    }

    /**
     * Toggle the filter.
     * @param animate Specify wether the animation should be played.
     * @since 4.1.0
     */
    public toggle(animate: boolean = true): void {
        if (this.filterCollapseLink.hasClass("collapsed")) {
            this.expand(animate);
        } else {
            this.collapse(animate);
        }
    }
}
